import "./style.scss";
import { getDateFormat, getDaysDiff, loadOrganizationLogos } from "../../utils";
import { AUDIT_LEVEL, AUDIT_MODES } from "../../constants";
import AuditeeInformation from "../../icons/auditeeInformation.png";
import AduitorInfo from "../../icons/auditorInfo.png";
import AduitorPref from "../../icons/auditpreference.png";
import { useState, useEffect } from "react";

interface TeamMember {
  id: string;
  name: string;
  email: string;
}

interface AuditeMember {
  email: string;
  name: string;
}
interface Teamlead {
  email: string;
  name: string;
}
interface OrganizationDetail {
  organizationId: number;
  name: string;
  type: string;
  applicationName: string;
  organizationEmail: string;
  teamLead: Teamlead[];
  members: AuditeMember[];
  logo?: string;
  organizationLogo?: string;
  createdBy?: string | null;
  createdDate?: Date | null;
  mappedAt?: Date;
  modifiedBy?: string | null;
  modifiedDate?: Date | null;
}
interface AuditData {
  teamMembers: TeamMember[];
  auditLevel: number;
  auditType: number;
  auditSla: number;
  escalationDay: number;
  auditeeInfo: {
    membersname?: string[]; // <-- add this
    membersmail?: string[];
    applicationName?: string[]; // <-- add this
    auditThemeNames?: string[];
    organizationdetail?: OrganizationDetail[];
    sequencedata?: any[];
    startDate: Date;
    startDateMarker: Date;
    endDate: Date;
    endDateMarker: Date;
    auditee: string;
    emails: AuditeMember[];
    auditScope: string;
    applicationDesc: string;
    auditeeTeamLead: Teamlead[];
    isTeamLead?: boolean;
  };
}

interface SequenceItem {
  sequenceId: number;
  name: string;
}

interface SequenceData {
  sequenceId: number;
  sequenceOrder: number;
  questionValidationRequired: boolean;
  responseValidationRequired: boolean;
}

interface Props {
  teamMembersDisplay: any;
  auditeeDisplay: string;
  StatusDot: any;
  userData: any;
  statusColorMap: any;
  auditData: AuditData;
  sequence?: SequenceItem[];
}
const getAuditorReviewText = (
  questionValidationRequired: boolean,
  responseValidationRequired: boolean,
): string => {
  if (!questionValidationRequired && !responseValidationRequired) {
    return "Fully automated (No manual review)";
  }

  const parts: string[] = [];
  if (questionValidationRequired) {
    parts.push("Before sending questions");
  }
  if (responseValidationRequired) {
    parts.push("After AI review responses");
  }

  return parts.join(", ");
};

const Step5ReviewandSubmit: React.FC<Props> = ({
  teamMembersDisplay,
  auditeeDisplay,
  StatusDot,
  statusColorMap,
  userData,
  auditData,
  sequence,
}) => {
  const [processedOrganizations, setProcessedOrganizations] = useState<
    OrganizationDetail[]
  >([]);
  const sequenceOrderMap = new Map(
    (auditData.auditeeInfo.sequencedata || []).map((item) => [
      item.sequenceId,
      item.sequenceOrder,
    ]),
  );

  const sequenceWithReview = (sequence || [])
    .map((seq) => {
      const auditSeq = auditData.auditeeInfo.sequencedata?.find(
        (s) => s.sequenceId === seq.sequenceId,
      );

      return {
        sequenceId: seq.sequenceId,
        name: seq.name,
        sequenceOrder: auditSeq?.sequenceOrder ?? null,
        reviewText: getAuditorReviewText(
          auditSeq?.questionValidationRequired ?? false,
          auditSeq?.responseValidationRequired ?? false,
        ),
      };
    })
    .filter((item) => item.sequenceOrder !== null)
    .sort((a, b) => a.sequenceOrder! - b.sequenceOrder!);

  // Group sequences by sequenceOrder
  const groupedSequences = sequenceWithReview.reduce(
    (acc, item) => {
      const existingGroup = acc.find(
        (group) => group.sequenceOrder === item.sequenceOrder,
      );
      if (existingGroup) {
        existingGroup.items.push(item);
      } else {
        acc.push({
          sequenceOrder: item.sequenceOrder,
          items: [item],
        });
      }
      return acc;
    },
    [] as { sequenceOrder: number; items: typeof sequenceWithReview }[],
  );

  // Process organizations to convert logo data to URLs
  useEffect(() => {
    const processOrganizationsWithLogos = async () => {
      const organizations = auditData.auditeeInfo?.organizationdetail;
      if (organizations && organizations.length > 0) {
        const logoMap = await loadOrganizationLogos(organizations);
        const orgsWithLogos = organizations.map((org) => ({
          ...org,
          organizationLogo:
            logoMap.get(org.organizationId) || org.organizationLogo,
        }));
        setProcessedOrganizations(orgsWithLogos);
      } else {
        setProcessedOrganizations(organizations || []);
      }
    };

    processOrganizationsWithLogos();
  }, [auditData.auditeeInfo?.organizationdetail]);
  return (
    <div className="content-card ">
      <div className="step-content">
        <div className="step-header">
          <h2>Review & Submit</h2>
        </div>

        <div className="step5align">
          <div className="d-flex gap-3">
            <div className="review-section marBottom w-100">
              <h3 className="review-title text-start">
                <img src={AduitorPref} alt="no img" />
                &nbsp;&nbsp;Audit Preferences
              </h3>

              <div className="review-grid-item text-start">
                {/* Row 1 */}

                <table className="w-100 no-border-table customLabeletcwidth ">
                  {/* This thead has been added to fix a SonarQube vulnerability. 
                        As per the SonarQube syntax no table should exist without the thead and row scope Since it is visually hidden, it will not affect the screen.  */}
                  <thead className="visually-hidden">
                    <tr>
                      <th>Column 1</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="label">Audit Themes:</td>
                      <td className="value">
                        {auditData?.auditeeInfo?.auditThemeNames?.join(", ") ||
                          "Not selected"}
                      </td>
                    </tr>
                    <tr>
                      <td className="label">Audit Level:</td>
                      <td className="value">
                        {auditData.auditLevel} (
                        {AUDIT_LEVEL[auditData.auditLevel as any] ||
                          "Not selected"}
                        ){" "}
                        <StatusDot
                          color={
                            statusColorMap[
                            AUDIT_LEVEL[auditData.auditLevel as any]
                            ]
                          }
                          size={12}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="label">Duration:</td>
                      <td className="value">
                        {getDateFormat(
                          auditData.auditeeInfo.startDate,
                          "DD-MM-YYYY",
                        )}{" "}
                        to{" "}
                        {getDateFormat(
                          auditData.auditeeInfo.endDate,
                          "DD-MM-YYYY",
                        )}{" "}
                        (
                        {getDaysDiff(
                          auditData.auditeeInfo.startDate,
                          auditData.auditeeInfo.endDate,
                        )}{" "}
                        days)
                      </td>
                    </tr>
                    <tr>
                      <td className="label"> Response Time:</td>
                      <td className="value">
                        {auditData.auditSla || "Not selected"} Day(s)
                      </td>
                    </tr>
                    <tr>
                      <td className="label">Reminder Alert:</td>
                      <td className="value">
                        {auditData.escalationDay || "Not selected"} Day(s)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="review-section marBottom w-100">
              <h3 className="review-title text-start">
                <img src={AduitorInfo} alt="no img" />
                &nbsp;&nbsp;Auditor Information
              </h3>
              <table className="w-100 no-border-table customLabeletcwidth ">
                <thead className="visually-hidden">
                  <tr>
                    <th>Column 1</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="label">Audit Team:</td>
                    <td className="value">
                      {userData.auditor || "Not provided"}
                    </td>
                  </tr>
                  <tr>
                    <td className="label">Members:</td>
                    <td className="value">
                      {auditData.auditeeInfo?.membersname &&
                        auditData.auditeeInfo.membersname.length > 0
                        ? auditData.auditeeInfo.membersname.map(
                          (name, index) => {
                            const email =
                              auditData.auditeeInfo?.membersmail?.[index];

                            return (
                              <div key={index}>
                                <strong>{name}</strong>
                                {email && (
                                  <>
                                    {" - "}
                                    {email}
                                  </>
                                )}
                              </div>
                            );
                          },
                        )
                        : "Not provided"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="review-section w-100">
            <h3 className="review-title text-start">
              <img src={AduitorPref} alt="no img" />
              &nbsp;&nbsp;Data Collection Workflow
            </h3>
            <table className="w-100 no-border-table customLabeletcwidth">
              <thead className="visually-hidden">
                <tr>
                  <th>Step</th>
                  <th>Auditor Review</th>
                </tr>
              </thead>

              <tbody>
                {groupedSequences.map((group, index) => (
                  <tr key={group.sequenceOrder}>
                    <td className="label">
                      Stage {index + 1} :{" "}
                      <span className="value">
                        {group.items.map((item) => item.name).join(", ")}
                      </span>
                    </td>
                    <td className="label ">
                      Auditor Review :{" "}
                      <span className="value">{group.items[0].reviewText}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="review-section text-nowrap">
            <div className="d-flex text-start mb-3 borderbottom">
              <h3 className="review-title">
                <img src={AuditeeInformation} alt="no img" /> Auditee
                Information
              </h3>
            </div>

            <div className="auditee-scroll-vertical">
              <div className="auditee-grid-vertical">
                {processedOrganizations.map((org, index) => (
                  <div
                    key={org.organizationId || index}
                    className="auditee-card"
                  >
                    <table className="w-100 no-border-table customLabeletcwidth my-2">
                      <thead className="visually-hidden">
                        <tr>
                          <th>Column 1</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="label">Organization:</td>
                          <td className="value">
                            {org.organizationLogo ? (
                              <img
                                src={org.organizationLogo}
                                alt={`${org.name} logo`}
                                className="auditee-logo"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="auditee-logo placeholder">
                                No Logo
                              </div>
                            )}
                            {org.name || "—"}
                          </td>
                        </tr>
                        <tr>
                          <td className="label">Application:</td>
                          <td className="value">
                            {Array.isArray(org.applicationName)
                              ? org.applicationName.join(", ")
                              : org.applicationName || "—"}
                          </td>
                        </tr>
                        <tr>
                          <td className="label">Auditee Team Lead:</td>
                          <td className="value">
                            <strong>{org.teamLead?.[0]?.name}</strong>
                            {org.teamLead?.[0]?.email && (
                              <>
                                {" - "}
                                {org.teamLead?.[0]?.email}
                              </>
                            )}
                          </td>
                        </tr>

                        <tr>
                          <td className="label">Auditee Members:</td>
                          <td className="value">
                            {org.members && org.members.length > 0
                              ? org.members
                                .filter(
                                  (member) =>
                                    !org.teamLead?.[0] ||
                                    member.email !== org.teamLead[0]?.email,
                                )
                                .map((member, i) => (
                                  <div key={i}>
                                    <strong>{member.name}</strong>
                                    {member.email && (
                                      <>
                                        {" - "}
                                        {member.email}
                                      </>
                                    )}
                                  </div>
                                ))
                              : "—"}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Step5ReviewandSubmit;
